import React from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  FaChartLine,
  FaSearch,
  FaLightbulb,
} from 'react-icons/fa';
import AISparkleIcon from '../../model-scenarios/components/AISparkleIcon';

const ICON_MAP = {
  trend: <FaChartLine size={18} style={{ color: '#7C3AED' }} />,
  anomaly: <FaSearch size={18} style={{ color: '#059669' }} />,
  recommendation: <FaLightbulb size={18} style={{ color: '#F59E0B' }} />,
};

const CALLOUT_STYLE = {
  success: {
    bg: '#ECFDF5',
    border: '#A7F3D0',
    text: '#065F46',
  },
  info: {
    bg: '#EFF6FF',
    border: '#BFDBFE',
    text: '#1E40AF',
  },
};

const WhiteBlock = ({ children }) => (
  <div
    className="mb-4 p-4"
    style={{
      background: '#FFFFFF',
      borderRadius: 14,
      border: '1px solid #F1E6FF',
    }}
  >
    {children}
  </div>
);

const SectionHeader = ({ icon, title }) => (
  <div className="d-flex align-items-center mb-3">
    <div className="mr-2">{ICON_MAP[icon]}</div>
    <div style={{ fontWeight: 600, fontSize: 16 }}>
      {title}
    </div>
  </div>
);

const RecommendationGroup = ({ group }) => {
  if (!group || !Array.isArray(group.items) || group.items.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="font-weight-bold mb-2">
        {group.label}:
      </div>
      <ul style={{ marginBottom: 0, paddingLeft: 20, color: '#334155', lineHeight: 1.75 }}>
        {group.items?.map((item, idx) => (
          <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const Callout = ({ block }) => {
  if (!block) return null;

  const style = CALLOUT_STYLE[block.variant] || CALLOUT_STYLE.info;

  return (
    <div
      className="mt-3 p-3"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: 10,
        color: style.text,
        fontSize: 14,
        lineHeight: 1.6,
      }}
    >
      {block.text}
    </div>
  );
};

const BulletList = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <ul className="mb-0 pl-3" style={{ color: '#334155', lineHeight: 1.75 }}>
      {items.map((item, idx) => (
        <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
      ))}
    </ul>
  );
};

const BenchmarkAIInsights = ({ insights, benchmarkSource }) => {
  const { t } = useTranslation();

  if (!insights || !Array.isArray(insights.sections) || insights.sections.length === 0) {
    return null;
  }

  const data = insights;
  const sectionTitleMap = {
    trend: t('Trend Analysis'),
    anomaly: t('Data Anomalies'),
    recommendation: t('Recommendations'),
  };
  const footerNote = t(
    'All emission intensity benchmarks and variance calculations are based on UK emission standards ({{source}}) and Carbon Reduction Plan guidance aligned with UK Climate Change Committee recommendations.',
    {
      source: benchmarkSource || 'DEFRA/BEIS 2024',
    }
  );

  return (
    <>
      <Card
        className="mt-4"
        style={{
          backgroundColor: '#F5F3FF',
          borderRadius: 16,
          border: '1px solid #E9D5FF',
          padding: 16,
          boxShadow: '0 0 0 1px rgba(124, 58, 237, 0.04), 0 8px 24px rgba(124, 58, 237, 0.06)',
        }}
      >
        <Card.Body>
          <div className="d-flex align-items-center mb-4">
            <AISparkleIcon size={22} color="#7C3AED" className="mr-2" />
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {data.title || t('AI-Powered Insights')}
            </div>
          </div>

          {data.sections.map((section, idx) => {
            if (section.type === 'trend') {
              return (
                <WhiteBlock key={idx}>
                  <SectionHeader
                    icon={section.type}
                    title={sectionTitleMap[section.type]}
                  />
                  <div style={{ color: '#374151', fontSize: 15, lineHeight: 1.7 }}>
                    {section.content.text}
                  </div>
                </WhiteBlock>
              );
            }

            if (section.type === 'anomaly') {
              return (
                <WhiteBlock key={idx}>
                  <SectionHeader
                    icon={section.type}
                    title={sectionTitleMap[section.type]}
                  />
                  {section.content?.text && <Callout block={section.content} />}
                  <div style={{ color: '#374151', fontSize: 15, marginTop: section.content?.text ? 12 : 0 }}>
                    <BulletList items={section.content?.items} />
                  </div>
                </WhiteBlock>
              );
            }

            if (section.type === 'recommendation') {
              const r = section.content;

              return (
                <WhiteBlock key={idx}>
                  <SectionHeader
                    icon={section.type}
                    title={sectionTitleMap[section.type]}
                  />
                  <RecommendationGroup group={r.immediate} />
                  <RecommendationGroup group={r.shortTerm} />
                  <RecommendationGroup group={r.strategic} />
                  <Callout block={r.alignmentNote} />
                </WhiteBlock>
              );
            }

            return null;
          })}

        </Card.Body>
      </Card>

      {footerNote && (
        <Card
          className="mt-3"
          style={{
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: 12,
            padding: 8,
          }}
        >
          <Card.Body style={{ fontSize: 13, color: '#1D4ED8', lineHeight: 1.6 }}>
            <strong>{t('Note')}:</strong> {footerNote}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default BenchmarkAIInsights;
